import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getWelcomeScreenHtml } from './welcomeScreenConfig';
import { useMemo, useRef, useState, useEffect, } from 'react';
import { Box, Button, IconButton, Stack, TextField, } from '@mui/material';
import { ArrowForwardIosOutlined as CircleArrowIcon, MicOutlined as MicOutlinedIcon, StopOutlined as StopIcon, RestartAlt as RestartAltIcon, } from '@mui/icons-material';
// import AssetImage from '@mcn-platform/portal-lib/src/portal/common/asset/AssetImage';
import SuggestedPrompts from './SuggestedPrompts';
import buildSx from './buildSx';
import { useConversationStore } from '../ConversationStoreProvider';
import { useTokens } from '../tokens/TokensProvider';
import Image from './Image';
import VoiceWaveAnimation from './VoiceWaveAnimation';
const ChatInput = ({ append, handleInputChange, handleSubmit, input, isLoading, reset, sendImage, style, 
// suggestedPrompts,
persistentPrompts, resetConversationLabel, enableVoiceInput, }) => {
    const store = useConversationStore();
    const tokens = useTokens();
    // const { stylesheet } = store.currentPortal;
    // const placeholder = store.currentPortal.clientAIPromptPlaceholder || ' How can I help?';
    const { stylesheet } = store;
    const hasCustomWelcome = Boolean(getWelcomeScreenHtml(store.clientAIConfig?.welcomeScreenHtml));
    const getTokenValue = (key) => {
        const tokenValue = tokens.getValue(key);
        if (!tokenValue || tokenValue.trim() === '' || tokenValue === `{{${key}}}`) {
            return '';
        }
        return tokenValue;
    };
    const placeholder = store.promptPlaceholder?.trim()
        ? store.promptPlaceholder
        : getTokenValue('promptPlaceholder');
    const inputSx = useMemo(() => (buildSx(style, stylesheet, {
    // flexGrow: '1',
    })), [style, stylesheet]);
    const [isRecording, setIsRecording] = useState(false);
    const [waveBufferState, setWaveBufferState] = useState([]); // for render only
    const waveBufferRef = useRef([]); // persistent buffer
    // const [interimTranscript, setInterimTranscript] = useState('');
    const shouldContinueRef = useRef(false);
    const recognitionRef = useRef(null);
    const transcriptChunksRef = useRef([]);
    const userInputRef = useRef('');
    useEffect(() => {
        userInputRef.current = input;
    }, [input]);
    useEffect(() => {
        let audioContext = null;
        let analyser = null;
        let dataArray;
        let source = null;
        let rafId;
        let stream = null;
        // Waveform buffer config - optimized for low latency
        const barsPerSecond = 120; // Increased to ensure buffer is large enough
        const bufferSeconds = 1.5; // Reduced from 3 to show only 1.5 seconds
        const bufferLength = barsPerSecond * bufferSeconds;
        const framesPerBar = 1; // Process every frame immediately for lowest latency
        // Accumulators for averaging
        let sum = 0;
        let count = 0;
        function updateLevels() {
            if (analyser && dataArray) {
                analyser.getByteTimeDomainData(dataArray);
                let frameSum = 0;
                // Optimized loop - process every 4th sample for better performance
                for (let i = 0; i < dataArray.length; i += 4) {
                    frameSum += Math.abs(dataArray[i] - 128);
                }
                const amplitude = Math.min(1, frameSum / (dataArray.length / 4) / 128);
                sum += amplitude;
                count += 1;
                if (count >= framesPerBar) {
                    const avg = sum / count;
                    sum = 0;
                    count = 0;
                    // Push to buffer
                    const buf = waveBufferRef.current;
                    buf.push(avg);
                    while (buf.length < bufferLength)
                        buf.unshift(0); // Always pad with zeros
                    if (buf.length > bufferLength)
                        buf.splice(0, buf.length - bufferLength);
                    setWaveBufferState([...buf]); // trigger re-render
                }
            }
            rafId = requestAnimationFrame(updateLevels);
        }
        if (isRecording && navigator.mediaDevices?.getUserMedia) {
            // Initialize buffer with zeros so the waveform starts empty
            waveBufferRef.current = Array(bufferLength).fill(0);
            setWaveBufferState([...waveBufferRef.current]);
            navigator.mediaDevices.getUserMedia({ audio: true }).then((mediaStream) => {
                stream = mediaStream;
                audioContext = new (window.AudioContext || window.webkitAudioContext)();
                analyser = audioContext.createAnalyser();
                analyser.fftSize = 256; // Reduced from 1024 for lower latency
                analyser.smoothingTimeConstant = 0.3; // Reduced smoothing for faster response
                dataArray = new Uint8Array(analyser.fftSize);
                source = audioContext.createMediaStreamSource(mediaStream);
                source.connect(analyser);
                updateLevels();
            }).catch(() => {
                waveBufferRef.current = Array(bufferLength).fill(0);
                setWaveBufferState([...waveBufferRef.current]);
            });
        }
        else if (!isRecording) {
            // Only clear the buffer when recording stops
            waveBufferRef.current = [];
            setWaveBufferState([]);
        }
        return () => {
            if (rafId)
                cancelAnimationFrame(rafId);
            if (source)
                source.disconnect();
            if (analyser)
                analyser.disconnect();
            if (audioContext)
                audioContext.close();
            if (stream)
                stream.getTracks().forEach((t) => t.stop());
        };
    }, [isRecording]);
    const submit = (event) => {
        // console.log('ChatInput onSubmit', event);
        event.preventDefault();
        if (isLoading) {
            return;
        }
        if (isRecording && recognitionRef.current) {
            recognitionRef.current.stop();
        }
        handleSubmit({});
    };
    const handleVoiceInput = () => {
        const LANG = 'en-US';
        const recognition = new (window.SpeechRecognition
            || window.webkitSpeechRecognition
            || window.mozSpeechRecognition
            || window.msSpeechRecognition)();
        recognition.lang = LANG;
        recognition.onresult = (event) => {
            let interim = '';
            for (let i = event.resultIndex; i < event.results.length; i += 1) {
                const result = event.results[i];
                if (result.isFinal) {
                    const cleaned = result[0].transcript.trim();
                    transcriptChunksRef.current.push(cleaned);
                }
                else {
                    interim += result[0].transcript;
                }
            }
            const updated = [userInputRef.current, ...transcriptChunksRef.current, interim]
                .map((part) => part.trim())
                .filter(Boolean)
                .join(' ');
            handleInputChange({
                target: { value: updated },
            });
        };
        recognition.onstart = () => {
            setIsRecording(true);
        };
        recognition.onend = () => {
            // If the user has not requested to stop, restart recognition for continuous listening
            if (shouldContinueRef.current && recognitionRef.current) {
                recognitionRef.current.start();
            }
            else {
                setIsRecording(false); // Only when the user actually stops
            }
        };
        shouldContinueRef.current = true;
        recognition.start();
        recognitionRef.current = recognition;
    };
    const stopVoiceInput = () => {
        transcriptChunksRef.current = [];
        shouldContinueRef.current = false;
        if (recognitionRef.current) {
            recognitionRef.current.stop();
        }
        setIsRecording(false);
    };
    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            submit(event);
        }
    };
    return (_jsxs(Stack, { direction: "column", sx: inputSx, spacing: 1, children: [_jsx(Stack, { direction: "row", spacing: 1, flexGrow: 1, children: _jsx(Box, { sx: { flexGrow: 1, display: 'flex', alignItems: 'center', minWidth: 0 }, children: isRecording ? (_jsxs(Box, { sx: {
                            width: '100%',
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            background: (theme) => theme.palette.background.paper,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: (theme) => theme.palette.divider,
                            padding: '0 14px',
                        }, children: [_jsx(VoiceWaveAnimation, { height: 40, levels: waveBufferState }), _jsx(IconButton, { type: "button", className: "site-system-button-primary", disabled: isLoading, sx: { marginLeft: 1, padding: '5px' }, onClick: stopVoiceInput, children: _jsx(StopIcon, {}) })] })) : (_jsx(TextField, { value: input, onChange: handleInputChange, onKeyPress: handleKeyPress, placeholder: placeholder, fullWidth: true, size: "small", disabled: isRecording, sx: {
                            minWidth: 0,
                            '& .MuiInputBase-root': {
                                fontSize: hasCustomWelcome && style?.fontSize ? `${style.fontSize}px` : '1rem',
                                ...(hasCustomWelcome && style?.borderRadius ? { borderRadius: `${style.borderRadius}px` } : {}),
                                paddingRight: '8px',
                            },
                            '& .MuiInputBase-input': {
                                overflowWrap: 'anywhere',
                            },
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                // borderColor: (t) => (t.portal?.global?.font?.color),
                                },
                            },
                        }, InputProps: {
                            endAdornment: (_jsxs(Stack, { direction: "row", spacing: 0.5, alignItems: "center", children: [enableVoiceInput && (_jsx(IconButton, { type: "button", className: "site-system-button-primary", disabled: isLoading, sx: { padding: '5px' }, onClick: handleVoiceInput, children: _jsx(MicOutlinedIcon, {}) })), sendImage ? (_jsx(IconButton, { ...(hasCustomWelcome ? {
                                            type: 'button',
                                            'aria-label': 'Send message',
                                            disabled: isLoading || input.trim() === '' || isRecording,
                                        } : {}), onClick: submit, children: _jsx(Image, { url: sendImage.url, status: sendImage.status, alt: sendImage.alt, className: sendImage.className, avatar: sendImage.avatar, avatarVariant: sendImage.avatarVariant, customStyle: { height: '32px', width: '32px', padding: '3px', ...(hasCustomWelcome ? sendImage.customStyle : {}) } }) })) : (_jsx(IconButton, { type: "submit", ...(hasCustomWelcome ? { 'aria-label': 'Send message' } : {}), className: "site-system-button-primary", disabled: isLoading || input.trim() === '' || isRecording, sx: { padding: '5px' }, onClick: submit, children: _jsx(CircleArrowIcon, {}) }))] })),
                        } })) }) }), (persistentPrompts || reset) && (_jsxs(Stack, { direction: "row", spacing: 1, alignContent: "center", children: [_jsx(SuggestedPrompts, { suggestedPrompts: persistentPrompts, append: append, isLoading: isLoading, chipStyle: "secondary" }), _jsx(Box, { flexGrow: 1 }), reset && (_jsxs(Button, { className: "site-system-button-secondary", onClick: reset, sx: {
                            justifyContent: {
                                xs: 'center',
                                sm: 'flex-start',
                            },
                            minWidth: {
                                xs: 40,
                                sm: 'auto',
                            },
                            padding: {
                                xs: '6px',
                                sm: 'default',
                            },
                            '& .reset-icon': {
                                display: {
                                    xs: 'inline-flex',
                                    sm: 'none',
                                },
                            },
                            '& .reset-label': {
                                display: {
                                    xs: 'none',
                                    sm: 'inline',
                                },
                            },
                            '& .MuiButton-startIcon': {
                                marginRight: {
                                    xs: 0,
                                    sm: '8px',
                                },
                            },
                        }, children: [_jsx(RestartAltIcon, { className: "reset-icon" }), _jsx("span", { className: "reset-label", children: resetConversationLabel || tokens.getToken('resetConversationLabel') })] }))] }))] }));
};
export default ChatInput;
//# sourceMappingURL=ChatInput.js.map